import type { JSX } from 'react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { usePendingEvaluations } from '@/features/memorization/hooks';
import { useHasCapability } from '@/lib/capabilities';

const PAGE_SIZE = 10;

/**
 * "Setoran Menunggu Evaluasi" (Phase G-3, Teacher Workspace). An org-wide,
 * unfiltered-by-student view of `GET /memorization/setoran?status=
 * AWAITING_EVALUATION` - a real, already-implemented status filter (used
 * per-student since G-2), not a fabricated "queue" endpoint (no such
 * endpoint exists anywhere in the backend - confirmed by a fresh route
 * search). Gated on `memorization_targets:manage`, the same broad-access
 * signal the backend itself uses to decide whether a caller's Setoran
 * reads are org-wide or Self/Child-narrowed (ADR-019 Decision 17, fixed
 * for Setoran specifically by PR #42) - a Student/Parent never sees this
 * card and the underlying query never fires for them.
 */
export function PendingEvaluationsCard(): JSX.Element | null {
  const [page, setPage] = useState(1);
  const canView = useHasCapability('memorization_targets:manage');
  const { data, isLoading, isError } = usePendingEvaluations(page, PAGE_SIZE, canView);

  if (!canView) {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Setoran Menunggu Evaluasi</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        {isLoading && (
          <div className="flex flex-col gap-2">
            <Skeleton className="h-14 w-full" />
            <Skeleton className="h-14 w-full" />
          </div>
        )}

        {isError && (
          <p className="text-sm text-destructive">Gagal memuat setoran menunggu evaluasi.</p>
        )}

        {!isLoading && !isError && data !== undefined && data.items.length === 0 && (
          <p className="text-sm text-muted-foreground">Tidak ada setoran yang menunggu evaluasi.</p>
        )}

        {!isLoading && !isError && data !== undefined && data.items.length > 0 && (
          <>
            <ul className="flex flex-col gap-2">
              {data.items.map((submission) => (
                <li key={submission.id}>
                  <Link to={`/students/${submission.studentId}/setoran/${submission.id}`}>
                    <div className="flex flex-col gap-2 rounded-md border border-border p-3 transition-colors hover:bg-secondary/50 sm:flex-row sm:items-center sm:justify-between">
                      <div className="flex min-w-0 flex-col gap-1">
                        <span className="text-sm font-medium">
                          Surah {submission.surah}: Ayat {submission.startAyah}
                          {submission.endAyah !== submission.startAyah
                            ? `-${submission.endAyah}`
                            : ''}
                        </span>
                        <span className="truncate font-mono text-xs text-muted-foreground">
                          {submission.studentId}
                        </span>
                      </div>
                      <Badge variant="secondary" className="w-fit">
                        {new Date(submission.submittedAt).toLocaleDateString('id-ID')}
                      </Badge>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>

            {data.total_pages > 1 && (
              <div className="flex items-center justify-between gap-4">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => {
                    setPage((current) => Math.max(1, current - 1));
                  }}
                >
                  Sebelumnya
                </Button>
                <span className="text-xs text-muted-foreground">
                  Halaman {data.page} dari {data.total_pages} ({data.total} total)
                </span>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={page >= data.total_pages}
                  onClick={() => {
                    setPage((current) => Math.min(data.total_pages, current + 1));
                  }}
                >
                  Berikutnya
                </Button>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
